/**
 * ncrp_complaint.v1 — client parser for Fusion Build (E8).
 * Schema: docs/research/FUSION_CASEWORK_CANVAS_SPEC.md Appendix B.
 */
import { canonicalEntityId, normalizeIdentifier } from '../entityJoin';

export interface NcrpTxn {
  utr?: string;
  amount_inr?: number;
  ts?: string;
  beneficiary_vpa?: string;
  beneficiary_account?: { acct?: string; ifsc?: string; bank?: string };
}

export interface NcrpComplaint {
  schema?: string;
  complaint_id?: string;
  victim?: { name?: string; phone?: string; state?: string };
  incident?: { category?: string; amount_inr?: number; narrative?: string };
  transactions?: NcrpTxn[];
}

export interface GraphBag {
  id: string;
  label: string;
  module: string;
  type: string;
  risk_score?: number;
  size?: number;
  step?: number;
  placement_role?: string;
  [key: string]: unknown;
}

export interface NcrpParseResult {
  bags: GraphBag[];
  complaintId: string;
  victimName?: string;
  producedType?: string;
  producedNodeId?: string;
  summary: string;
}

function acctLabel(a: { acct?: string; ifsc?: string; bank?: string }): string {
  const acct = (a.acct || '').trim();
  const ifsc = (a.ifsc || '').trim();
  const bank = (a.bank || '').trim();
  if (acct && ifsc) return `${bank ? bank + ' ' : ''}••${acct.slice(-4)} (${ifsc})`;
  if (acct) return `••${acct.slice(-4)}`;
  return ifsc || 'beneficiary account';
}

/** Parse & validate ncrp_complaint.v1 JSON text or object. Throws on bad input. */
export function parseNcrpComplaint(raw: unknown): NcrpParseResult {
  let doc: NcrpComplaint;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) throw new Error('Paste an NCRP complaint JSON (ncrp_complaint.v1)');
    try {
      doc = JSON.parse(t) as NcrpComplaint;
    } catch {
      throw new Error('Invalid JSON — expected ncrp_complaint.v1');
    }
  } else if (raw && typeof raw === 'object') {
    doc = raw as NcrpComplaint;
  } else {
    throw new Error('Paste an NCRP complaint JSON (ncrp_complaint.v1)');
  }

  const schema = String(doc.schema || '').trim();
  if (schema && schema !== 'ncrp_complaint.v1') {
    throw new Error(`Unsupported schema "${schema}" — expected ncrp_complaint.v1`);
  }

  const complaintId = String(doc.complaint_id || 'UNKNOWN').trim() || 'UNKNOWN';
  const victim = doc.victim || {};
  const txns = Array.isArray(doc.transactions) ? doc.transactions : [];
  const bags: GraphBag[] = [];

  const complaintNodeId = `ent:document:ncrp:${normalizeIdentifier('document', complaintId) || complaintId}`;
  bags.push({
    id: complaintNodeId,
    label: `NCRP ${complaintId}`,
    module: 'shared',
    type: 'document',
    risk_score: 0.4,
    size: 6,
    step: 1,
    placement_role: 'source',
    complaint_id: complaintId,
    category: doc.incident?.category,
    amount_inr: doc.incident?.amount_inr,
    narrative: doc.incident?.narrative,
    attribution: { source: 'ncrp_complaint.v1', method: 'parse', confidence: 1 },
  });

  let victimPersonId: string | undefined;
  if (victim.name?.trim()) {
    victimPersonId = canonicalEntityId('person', victim.name.trim());
    bags.push({
      id: victimPersonId,
      label: victim.name.trim(),
      module: 'shared',
      type: 'person',
      risk_score: 0.2,
      size: 7,
      step: 1,
      placement_role: 'source',
      state: victim.state,
      attribution: { source: 'ncrp_complaint.v1', method: 'victim', confidence: 0.95 },
    });
    bags.push({
      id: `ncrp:edge:complaint~victim:${complaintId}`,
      label: 'complaint',
      module: 'shared',
      type: 'edge',
      source: complaintNodeId,
      target: victimPersonId,
      edge_type: 'cross_link',
      determinism: 'deterministic',
      step: 1,
      _edgeLabel: 'victim',
    });
  }

  let phoneId: string | undefined;
  if (victim.phone?.trim()) {
    const phoneNorm = normalizeIdentifier('phone', victim.phone);
    if (phoneNorm) {
      phoneId = canonicalEntityId('phone', victim.phone);
      bags.push({
        id: phoneId,
        label: phoneNorm,
        module: 'network',
        type: 'phone',
        risk_score: 0.25,
        size: 4,
        step: 1,
        attribution: { source: 'ncrp_complaint.v1', method: 'victim_phone', confidence: 0.95 },
      });
      if (victimPersonId) {
        bags.push({
          id: `ncrp:edge:victim~phone:${phoneNorm}`,
          label: 'phone',
          module: 'shared',
          type: 'edge',
          source: victimPersonId,
          target: phoneId,
          edge_type: 'cross_link',
          determinism: 'deterministic',
          step: 1,
          _edgeLabel: 'phone number',
        });
      }
    }
  }

  let producedType: string | undefined;
  let producedNodeId: string | undefined;
  let beneficiaryCount = 0;

  txns.forEach((tx, i) => {
    const amount = Number(tx.amount_inr) || 0;
    const utr = (tx.utr || '').trim();
    const edgeLabel = utr
      ? `UTR ${utr.slice(0, 8)}… · ₹${amount.toLocaleString('en-IN')}`
      : `₹${amount.toLocaleString('en-IN')}`;

    let sinkId: string | undefined;
    let sinkType: string | undefined;

    if (tx.beneficiary_vpa?.trim()) {
      const vpa = tx.beneficiary_vpa.trim();
      sinkId = canonicalEntityId('vpa', vpa);
      sinkType = 'vpa';
      if (!bags.some((b) => b.id === sinkId)) {
        bags.push({
          id: sinkId,
          label: vpa.toLowerCase(),
          module: 'bank',
          type: 'vpa',
          risk_score: 0.55,
          size: 5,
          step: 1,
          placement_role: 'sink',
          attribution: { source: 'ncrp_complaint.v1', method: 'beneficiary_vpa', confidence: 1 },
        });
        beneficiaryCount++;
      }
    } else if (tx.beneficiary_account?.acct || tx.beneficiary_account?.ifsc) {
      const acct = tx.beneficiary_account;
      const key = normalizeIdentifier('account', `${acct.ifsc || ''}:${acct.acct || ''}`)
        || `acct-${i}`;
      sinkId = `ent:account:${key}`;
      sinkType = 'account';
      if (!bags.some((b) => b.id === sinkId)) {
        bags.push({
          id: sinkId,
          label: acctLabel(acct),
          module: 'bank',
          type: 'account',
          risk_score: 0.55,
          size: 5,
          step: 1,
          placement_role: 'sink',
          account_ref: acct.acct,
          ifsc: acct.ifsc,
          bank: acct.bank,
          attribution: { source: 'ncrp_complaint.v1', method: 'beneficiary_account', confidence: 1 },
        });
        beneficiaryCount++;
      }
    }

    if (!sinkId) return;

    const src = victimPersonId || complaintNodeId;
    bags.push({
      id: `ncrp:edge:txn:${complaintId}:${i}:${utr || i}`,
      label: edgeLabel,
      module: 'bank',
      type: 'edge',
      source: src,
      target: sinkId,
      edge_type: 'trace',
      determinism: 'deterministic',
      step: 1,
      value: amount,
      unit: '₹',
      utr: utr || undefined,
      ts: tx.ts,
      _edgeLabel: edgeLabel,
    });

    if (!producedNodeId && sinkType) {
      producedType = sinkType;
      producedNodeId = sinkId;
    }
  });

  const summary = [
    `NCRP ${complaintId}`,
    victim.name ? `victim ${victim.name}` : null,
    `${txns.length} txn(s)`,
    `${beneficiaryCount} beneficiary node(s)`,
  ].filter(Boolean).join(' · ');

  return {
    bags,
    complaintId,
    victimName: victim.name?.trim(),
    producedType,
    producedNodeId,
    summary,
  };
}
